import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/common';

const navLinks: Array<{ path: string; label: string }> = [
  { path: '/', label: 'Home' },
  { path: '/about-ddarp', label: 'About DDARP' },
  { path: '/about-us', label: 'About Us' },
];

export const PublicLayout = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isTransparent = false;

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50">
        <div
          className={cn(
            'backdrop-blur-lg transition-all duration-300',
            isTransparent
              ? 'bg-transparent border-b border-transparent'
              : 'bg-white/95 border-b border-gray-200 shadow-sm'
          )}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              {/* Logo */}
              <Link to="/" className="flex items-center gap-2" aria-label="DDARP home">
                <img
                  src="/images/logos/ddarp-icon.svg"
                  alt=""
                  aria-hidden
                  className="w-8 h-8"
                  width={32}
                  height={32}
                />
                <span className={cn(
                  'text-xl font-bold transition-colors duration-300',
                  isTransparent ? 'text-white' : 'text-gray-900'
                )}>
                  DDARP
                </span>
              </Link>

              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center gap-1">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      cn(
                        'px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-300',
                        isTransparent
                          ? isActive
                            ? 'text-white bg-white/15'
                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                          : isActive
                            ? 'text-primary bg-primary-50'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      )
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
              </nav>

              {/* Login Button */}
              <div className="hidden md:flex items-center gap-4">
                <Link to="/login">
                  <Button
                    variant={isTransparent ? 'outline' : 'outline'}
                    size="sm"
                    className={cn(
                      'transition-all duration-300',
                      isTransparent && 'border-white/30 text-white hover:bg-white/10 hover:border-white/50'
                    )}
                  >
                    Login
                  </Button>
                </Link>
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
                className={cn(
                  'md:hidden p-2 transition-colors duration-300',
                  isTransparent ? 'text-white/70 hover:text-white' : 'text-gray-400 hover:text-gray-700'
                )}
              >
                {isMobileMenuOpen ? <X size={24} aria-hidden /> : <Menu size={24} aria-hidden />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-b border-gray-200 shadow-lg"
            >
              <nav className="px-4 py-4 space-y-2">
                {navLinks.map((link) => (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'block px-4 py-3 rounded-lg text-sm font-medium transition-colors',
                        isActive
                          ? 'text-primary bg-primary-50'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                      )
                    }
                  >
                    {link.label}
                  </NavLink>
                ))}
                <Link
                  to="/login"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block"
                >
                  <Button variant="outline" size="sm" fullWidth className="mt-4">
                    Login
                  </Button>
                </Link>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main className="flex-1 pt-16">
        <Outlet />
      </main>

      {/* Footer */}
      <footer style={{ backgroundColor: '#0d063e' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="col-span-1 md:col-span-2">
              <Link to="/" className="flex items-center gap-2 mb-4" aria-label="DDARP home">
                <img
                  src="/images/logos/ddarp-icon.svg"
                  alt=""
                  aria-hidden
                  className="w-8 h-8"
                  width={32}
                  height={32}
                />
                <span className="text-xl font-bold text-white">DDARP</span>
              </Link>
              <p className="text-white/80 text-sm mb-4">
                Distributed Detection And Routing Protocol
                <br />
                Doppler radar for the Internet
              </p>
              <p className="text-white/60 text-xs">
                A product of MadHat Media, Inc.
                <br />
                265 Franklin Street, Suite 1702
                <br />
                Boston, MA 02110
              </p>
            </div>

            {/* Navigation */}
            <div>
              <h3 className="text-white font-semibold mb-4">Navigation</h3>
              <ul className="space-y-2">
                {navLinks.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-white/70 hover:text-white text-sm transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* About */}
            <div>
              <h3 className="text-white font-semibold mb-4">About</h3>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about-us#company"
                    className="text-white/70 hover:text-white text-sm transition-colors"
                  >
                    Our Company
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about-ddarp#thesis-detail"
                    className="text-white/70 hover:text-white text-sm transition-colors"
                  >
                    Observation Thesis
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about-ddarp#substrate"
                    className="text-white/70 hover:text-white text-sm transition-colors"
                  >
                    118-Node Substrate
                  </Link>
                </li>
                <li>
                  <Link
                    to="/about-ddarp#patent"
                    className="text-white/70 hover:text-white text-sm transition-colors"
                  >
                    IP &amp; Patents
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className="text-white/70 hover:text-white text-sm transition-colors"
                  >
                    Contact
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          {/* Copyright */}
          <div className="border-t border-white/20 mt-8 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/70 text-sm">
              &copy; 2014-2026 MadHat Media, Inc. All rights reserved.
            </p>
            <p className="text-white/50 text-xs">
              Delaware C-Corporation | Founded 2014
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
